import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { isEmpty, isNil } from 'lodash'

import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import {
  fetchWebhookSubscription,
  createWebhookSubscription,
  updateWebhookSubscription,
  rotateWebhookSecret,
} from '@/reducers/organizations/webhookSubscription.actions'

// Hand-mirrored from WebhookSubscription.VALID_EVENT_TYPES (database-models); a stale
// entry is a 422 on save.
const EVENT_OPTIONS = [
  { value: 'call.transcript_ready', text: 'call.transcript_ready — transcript is available' },
  { value: 'call.ended', text: 'call.ended — the call hung up (transcript may not be ready)' },
  {
    value: 'call.transcript_skipped',
    text: 'call.transcript_skipped — call not transcribed (too short or too long)',
  },
  {
    value: 'call.transcript_failed',
    text: 'call.transcript_failed — transcription failed',
  },
]

// Fast feedback only; src/helpers/webhook_url.py is the source of truth and also resolves
// DNS.
//
// `@` is forbidden only in the AUTHORITY, which is what the server checks. Rejecting it
// anywhere killed valid URLs like .../balto?notify=ops@example.com — Save stayed disabled
// with a misleading message.
const HTTPS_URL_RE = /^https:\/\/[^\s/@]+(\/[^\s]*)?$/i

export const WebhookSubscriptionForm = ({ organizationId, onClose }) => {
  const dispatch = useDispatch()
  const [subscription, setSubscription] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  // Stored encrypted after this — lose it and you rotate.
  const [revealedSecret, setRevealedSecret] = useState(null)

  const reload = async (isMounted = () => true) => {
    try {
      const found = await dispatch(fetchWebhookSubscription({ organizationId }))
      if (isMounted()) {
        setSubscription(found)
        setLoadError(false)
      }
    } catch (error) {
      if (isMounted()) {
        setLoadError(true)
      }
    } finally {
      if (isMounted()) {
        setLoaded(true)
      }
    }
  }

  useEffect(() => {
    let active = true
    reload(() => active)
    return () => {
      active = false
    }
  }, [organizationId, dispatch])

  if (!loaded) {
    return <div>Loading…</div>
  }

  if (loadError) {
    return (
      <div className="field-help small-text">
        Could not load the webhook subscription. Close and reopen to retry.
      </div>
    )
  }

  const isEdit = Boolean(subscription)

  const handleSave = async (values) => {
    if (isEdit) {
      const updated = await dispatch(
        updateWebhookSubscription({
          organizationId,
          patch: { url: values.url, event_types: values.event_types },
          successMessage: 'Webhook subscription saved',
        })
      )
      if (updated) {
        await reload()
      }
      return
    }

    // Inactive so the endpoint and payload get reviewed before anything is delivered.
    const created = await dispatch(
      createWebhookSubscription({
        organizationId,
        url: values.url,
        eventTypes: values.event_types,
        active: false,
      })
    )
    if (created) {
      setRevealedSecret(created.signing_secret)
      setSubscription(created.subscription)
    }
  }

  const handleToggleActive = async () => {
    const goingLive = !subscription.active
    if (
      goingLive &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Activating starts sending your call events to this endpoint. Confirm the URL is ' +
          'correct and that your endpoint verifies the signature before trusting a request.'
      )
    ) {
      return
    }
    const updated = await dispatch(
      updateWebhookSubscription({
        organizationId,
        patch: { active: goingLive },
        successMessage: goingLive ? 'Webhook activated' : 'Webhook deactivated',
      })
    )
    if (updated) {
      await reload()
    }
  }

  const handleRotate = async () => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Rotating takes effect immediately: the next event is signed with the new secret. ' +
          'Deploy the new secret to your endpoint first, or signature verification will fail.'
      )
    ) {
      return
    }
    const rotated = await dispatch(rotateWebhookSecret({ organizationId }))
    if (rotated) {
      setRevealedSecret(rotated.signing_secret)
      setSubscription(rotated.subscription)
    }
  }

  // Via `raw` so it lands below the fields: at the top it pushed the fields the operator
  // came to edit under a status strip + secret dump.
  const statusCard = !isEdit ? null : (
    <Card
      key="webhook-status"
      compact
      className="mt"
      title="Status"
      subtitle={
        isNil(subscription.created_at)
          ? undefined
          : `Created ${new Date(subscription.created_at).toLocaleString()}`
      }
      pills={
        <Pill success={subscription.active} critical={!subscription.active} small>
          {subscription.active ? 'Active' : 'Inactive'}
        </Pill>
      }
      buttons={
        <div className="flex small-gap">
          <Button
            type="button"
            critical={subscription.active}
            success={!subscription.active}
            onClick={handleToggleActive}
          >
            {subscription.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button type="button" onClick={handleRotate}>
            Rotate secret
          </Button>
        </div>
      }
    >
      {isEmpty(revealedSecret) ? (
        <p className="small-text muted-text no-margin">
          The signing secret is stored encrypted and cannot be displayed again. Rotate to issue a
          new one.
        </p>
      ) : (
        <>
          <div className="flex flex-align-center small-gap mbs">
            <span className="bold small-text">Signing secret</span>
            <Pill warning small>
              Copy this now
            </Pill>
          </div>
          <p className="small-text muted-text">
            Shown once and cannot be retrieved again. Store it somewhere safe; if it is lost, rotate
            to issue a new one.
          </p>
          <code data-testid="revealed-signing-secret" className="force-wrap">
            {revealedSecret}
          </code>
        </>
      )}
    </Card>
  )

  const schema = [
    {
      type: 'text',
      name: 'url',
      label: 'Endpoint URL',
      required: true,
      placeholder: 'https://hooks.example.com/balto',
      description:
        'HTTPS only, reachable from the public internet. The server also rejects private ' +
        'addresses and URLs containing credentials.',
      customValidators: [
        Yup.string()
          .required('Endpoint URL is required')
          .matches(HTTPS_URL_RE, 'Must be an https:// URL with no credentials before the host'),
      ],
    },
    {
      type: 'checkboxGroup',
      name: 'event_types',
      label: 'Events',
      required: true,
      inline: false,
      options: EVENT_OPTIONS,
      defaultValue: [],
      description: 'Only the events selected here are delivered.',
      customValidators: [Yup.array().min(1, 'Select at least one event')],
    },
    ...(isEdit ? [{ raw: true, name: 'status', as: statusCard }] : []),
  ]

  const existingValues = isEdit
    ? { url: subscription.url, event_types: subscription.event_types || [] }
    : undefined

  return (
    <AbstractForm
      schema={schema}
      onSubmit={handleSave}
      onClose={onClose}
      isModal
      buttonLabel="Save"
      closeButtonLabel="Close"
      existingValues={existingValues}
    />
  )
}
